import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import V2Shell, { ShellSwitcher } from './components/V2Shell';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { SwitcherItem } from './components/SwitcherMenu';
import {
    IconOverview,
    IconGallery,
    IconCapture,
    IconMedia,
    IconShare,
    IconProcessing,
    IconMore,
} from './components/icons';

/*
 * Event-level chrome. The context switcher is the job when the
 * activation belongs to one (its stops are one tap away — multi-day /
 * multi-stop runs), otherwise a recent-activations switcher. The event
 * dock (Overview · Gallery · Sharing · …) hosts the event's surfaces;
 * the page owns the event title and its job data lives on Overview.
 */

export default function V2EventChrome() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { activationId } = useParams();
    const id = activationId ? +activationId : 0;
    const base = `/v2/activations/${id}`;

    const { getActivation } = useActivation();
    const { getActivations } = useActivations();
    const { getProductions } = useProduction();

    const [switcher, setSwitcher] = useState<ShellSwitcher>({
        label: 'Activations',
        items: [],
    });

    useEffect(() => {
        let mounted = true;
        (async () => {
            const footer: SwitcherItem[] = [
                {
                    key: 'all',
                    label: 'All activations',
                    onClick: () => navigate('/v2/activations'),
                },
                {
                    key: 'jobs',
                    label: 'All jobs',
                    onClick: () => navigate('/v2/jobs'),
                },
            ];

            const activation = await getActivation(id).catch(() => null);

            if (mounted && activation?.jobId) {
                const stops = await getProductions({
                    jobId: +activation.jobId,
                }).catch(() => []);
                if (!mounted) return;
                if (stops?.length) {
                    setSwitcher({
                        prefix: 'Jobs /',
                        label: stops[0].jobName ?? `#${activation.jobId}`,
                        header: 'Stops on this job',
                        searchable: true,
                        items: stops.map((stop: any) => ({
                            key: String(stop.activationId),
                            label: stop.startAt
                                ? moment(stop.startAt).format('MMM D')
                                : 'TBD',
                            sublabel:
                                stop.venueName ?? stop.name ?? undefined,
                            active: +stop.activationId === id,
                            onClick: () =>
                                navigate(
                                    `/v2/activations/${stop.activationId}`,
                                ),
                        })),
                        footer,
                    });
                    return;
                }
            }

            /* no job — recent events for quick switching */
            const rows = await getActivations().catch(() => []);
            if (!mounted) return;
            const recent = (rows ?? [])
                .filter((row: any) => !row.isArchived)
                .sort(
                    (a: any, b: any) =>
                        moment(b.date).valueOf() - moment(a.date).valueOf(),
                )
                .slice(0, 8);
            setSwitcher({
                label: 'Activations',
                header: 'Recent events',
                searchable: true,
                items: recent.map((row: any) => ({
                    key: String(row.id),
                    label: row.name,
                    sublabel: row.date
                        ? moment(row.date).format('MMM D')
                        : undefined,
                    active: +row.id === id,
                    onClick: () => navigate(`/v2/activations/${row.id}`),
                })),
                footer,
            });
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <V2Shell
            switcher={switcher}
            items={[
                {
                    key: 'overview',
                    title: 'Overview',
                    icon: <IconOverview />,
                    active: pathname === base,
                    onClick: () => navigate(base),
                },
                {
                    key: 'gallery',
                    title: 'Gallery',
                    icon: <IconGallery />,
                    active: pathname === `${base}/gallery`,
                    onClick: () => navigate(`${base}/gallery`),
                },
                {
                    key: 'sharing',
                    title: 'Sharing',
                    icon: <IconShare />,
                    active: pathname.startsWith(`${base}/sharing`),
                    onClick: () => navigate(`${base}/sharing`),
                },
                {
                    key: 'capture',
                    title: 'Capture Experience',
                    icon: <IconCapture />,
                    active: pathname === `${base}/capture`,
                    onClick: () => navigate(`${base}/capture`),
                },
                {
                    key: 'processing',
                    title: 'Processing',
                    icon: <IconProcessing />,
                    active: pathname.startsWith(`${base}/processing`),
                    onClick: () => navigate(`${base}/processing`),
                },
                {
                    key: 'media',
                    title: 'Media',
                    icon: <IconMedia />,
                    active: pathname === `${base}/media`,
                    onClick: () => navigate(`${base}/media`),
                },
            ]}
            actions={[
                {
                    key: 'more',
                    title: 'Everything else (classic dashboard)',
                    icon: <IconMore />,
                    onClick: () => navigate(`/activations/${id}`),
                },
            ]}
        />
    );
}
